import styled, { css } from 'styled-components';
import { Text } from 'components/primitives/typography';
import { FlexAlignCenter, FlexCol } from 'components/primitives/flex';
import upperFirst from 'lodash/upperFirst';
import dayjs from 'dayjs';
import { mediaMSmMax } from 'helpers/breakpoints';
import { transformBreakLineDescription } from 'components/add-ons/helpers';
import { useUserDetails } from 'auth/mutations/userHooks';
import Avatar from 'components/ui/Avatar/Avatar';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { determineStaticPreviewImage } from 'utils/images';

export default function DefaultMessage({ message, isSpam = false }) {
  const { userId } = useUserDetails();
  const isCurrentUserSender = message.sender.id === userId;
  const userType = message?.sender?.user_type?.toLowerCase();

  return (
    <FlexCol mb="22px">
      <MessageBody
        isFromMe={isCurrentUserSender}
        isAdmin={!isCurrentUserSender && userType === 'admin'}
        isRenter={!isCurrentUserSender && userType === 'renter'}
        isCaptain={!isCurrentUserSender && userType === 'captain'}
        isOwner={!isCurrentUserSender && (userType === 'owner' || userType === 'boat manager')}
      >
        {transformBreakLineDescription(message.body)}
      </MessageBody>
      <MessageFooter isMe={isCurrentUserSender}>
        <Avatar
          diameter={32}
          profileLink={`/users/${message.sender.id}`}
          src={determineStaticPreviewImage(message.sender.picture.url, message.sender.first_name)}
        />
        <TextInfo gap="6px">
          {isSpam && (
            <SpamWarning>
              <SpamText>Undeliverable</SpamText>
              <Tooltip
                trigger="click"
                triggerWith={
                  <span data-testid="spam-warning-tooltip-trigger">
                    <Warning />
                  </span>
                }
              >
                <div style={{ maxWidth: '218px' }}>
                  <div>To protect users, messages that contain certain website links and words are not allowed.</div>
                </div>
              </Tooltip>
            </SpamWarning>
          )}
          {message.sender.first_name} <Dot />
          {!isCurrentUserSender && (
            <>
              <Text color="#8e9697" as="span">
                {upperFirst(message.sender.user_type)}
              </Text>
              <Dot />
            </>
          )}
          <Text color="#8e9697" as="span">
            {dayjs(message.created_at).isSame(dayjs(), 'day')
              ? dayjs(message.created_at).format('h:mm A')
              : dayjs(message.created_at).format('M/D/YYYY')}
          </Text>
        </TextInfo>
      </MessageFooter>
    </FlexCol>
  );
}

const MessageBody = styled.div<{
  isFromMe: boolean;
  isAdmin: boolean;
  isRenter: boolean;
  isCaptain: boolean;
  isOwner: boolean;
}>`
  padding: 24px;
  margin-bottom: 4px;
  background-color: #f2f2f2;

  ${mediaMSmMax} {
    overflow-wrap: anywhere;
  }

  ${({ isFromMe }) =>
    isFromMe
      ? css`
          border-radius: 16px 16px 4px 16px;
          margin-right: 44px;
          align-self: flex-end;
        `
      : css`
          border-radius: 16px 16px 16px 4px;
          margin-left: 44px;
          align-self: flex-start;
        `};

  ${({ isAdmin }) =>
    isAdmin &&
    css`
      background: rgba(7, 81, 194, 0.1);
    `};

  ${({ isRenter }) =>
    isRenter &&
    css`
      background-color: #102a5e;
      color: #fff;
    `};

  ${({ isCaptain }) =>
    isCaptain &&
    css`
      background-color: #004e60;
      color: #fff;
    `};

  ${({ isOwner }) =>
    isOwner &&
    css`
      background-color: #0e7873;
      color: #fff;
    `};
`;

const MessageFooter = styled(FlexAlignCenter)<{ isMe: boolean }>`
  font-size: 12px;
  font-weight: 500;
  gap: 12px;

  ${({ isMe }) =>
    isMe &&
    css`
      justify-content: flex-end;
      ${TextInfo} {
        order: -1;
      }
    `}
`;

const TextInfo = styled(FlexAlignCenter)`
  justify-content: flex-end;
`;

const Dot = styled.div`
  display: inline-block;
  width: 2px;
  height: 2px;
  border-radius: 50%;

  background: #17233c;
`;

const SpamWarning = styled(FlexAlignCenter)`
  gap: 4px;
  margin-right: 4px;
`;

const SpamText = styled.div`
  font-size: 12px;
  color: #bc350a;
  font-weight: 600;
`;

const Warning = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.49999 0C3.36181 0 0 3.36933 0 7.49999C0 11.6382 3.36934 15 7.49999 15C11.6382 15 15 11.6306 15 7.49999C15.0075 3.36933 11.6382 0 7.49999 0ZM7.49999 13.8769C3.98743 13.8769 1.13065 11.0201 1.13065 7.50753C1.13065 3.99497 3.98743 1.1382 7.49999 1.1382C11.0126 1.1382 13.8693 3.99497 13.8693 7.50753C13.8769 11.0201 11.0126 13.8769 7.49999 13.8769ZM8.06532 7.40954V11.3291C8.06532 11.6382 7.80904 11.8945 7.49999 11.8945C7.19095 11.8945 6.93467 11.6382 6.93467 11.3291V7.40954C6.93467 7.10049 7.19095 6.84424 7.49999 6.84424C7.80904 6.84424 8.06532 7.10049 8.06532 7.40954ZM8.57034 4.19095C8.57034 4.77889 8.08793 5.26132 7.49999 5.26132C6.91206 5.26132 6.42964 4.77889 6.42964 4.19095C6.42964 3.60301 6.91206 3.12062 7.49999 3.12062C8.08793 3.12062 8.57034 3.60301 8.57034 4.19095Z"
      fill="#BC350A"
    />
  </svg>
);
